import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  read: boolean;
  createdAt: Date;
  data?: any;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  markAsRead: (notificationId: string) => void;
  clearNotifications: (notificationIds?: string[]) => void;
  refetch: () => void;
  forceReconnect: () => void;
}

const useNotifications = (userId?: string): UseNotificationsReturn => {
  console.log('🔔 useNotifications hook called!', {
    userId,
    hasUserId: !!userId,
    timestamp: new Date().toISOString()
  });
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const WS_URL = API_URL.replace(/^http/, 'ws') + '/ws';
  
  console.log('🌐 WebSocket URL construction:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    API_URL,
    WS_URL
  });

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { userid: userId },
        params: { limit: 50 }
      });

      if (response.data.success) {
        setNotifications(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [userId, API_URL]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!userId) {
      console.log('❌ Cannot connect WebSocket: No user ID');
      return;
    }
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected');
      return;
    }

    const token = localStorage.getItem('token') || '';
    console.log('🔌 Attempting WebSocket connection...', {
      userId,
      wsUrl: WS_URL,
      hasToken: !!token,
      tokenLength: token.length,
      tokenStart: token.substring(0, 20) + '...'
    });

    if (!token) {
      console.error('❌ No token found in localStorage');
      return;
    }

    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`;
    console.log('🌐 Full WebSocket URL:', wsUrl.substring(0, 50) + '...');
    
    console.log('🚀 Creating WebSocket connection...');
    const ws = new WebSocket(wsUrl);
    console.log('📡 WebSocket object created:', {
      url: ws.url,
      readyState: ws.readyState,
      protocol: ws.protocol
    });

    ws.onopen = () => {
      console.log('✅ WebSocket connected successfully');
      setIsConnected(true);
      
      // Get initial notifications
      ws.send(JSON.stringify({ type: 'getNotifications' }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 Received WebSocket message:', data.type, data);
        
        switch (data.type) {
          case 'notification':
            console.log('🔔 New notification received:', data.data);
            // Add new notification
            setNotifications(prev => [data.data, ...prev]);
            
            // Show browser notification if permission granted
            if (Notification.permission === 'granted' && data.data.priority === 'high') {
              new Notification(data.data.title, {
                body: data.data.message,
                icon: '/24lp-logo-favicon.png'
              });
            }
            break;
            
          case 'notifications':
            console.log('📋 Initial notifications received:', data.data.length);
            // Set initial notifications
            setNotifications(data.data);
            break;
            
          case 'connected':
          case 'heartbeat':
          case 'pong':
            // Connection management messages
            console.log('💓 Connection management message:', data.type);
            break;
            
          default:
            console.log('❓ Unknown WebSocket message type:', data.type);
        }
      } catch (error) {
        console.error('❌ WebSocket message error:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    ws.onclose = (event) => {
      console.log('🔌 WebSocket disconnected:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      setIsConnected(false);
      wsRef.current = null;
      
      // Attempt to reconnect after 5 seconds
      console.log('🔄 Scheduling reconnection in 5 seconds...');
      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('🔄 Attempting to reconnect...');
        connectWebSocket();
      }, 5000);
    };

    wsRef.current = ws;
  }, [userId, WS_URL]);

  // Force reconnection function
  const forceReconnect = useCallback(() => {
    console.log('🔄 Force reconnecting WebSocket...');
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
    
    // Connect after a short delay
    setTimeout(() => {
      connectWebSocket();
    }, 1000);
  }, [connectWebSocket]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    // Update local state
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );

    // Send to server via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'markAsRead',
        notificationId
      }));
    }

    // Also update via API
    axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {
      userId
    }).catch(error => console.error('Error marking notification as read:', error));
  }, [userId, API_URL]);

  // Clear notifications
  const clearNotifications = useCallback((notificationIds?: string[]) => {
    if (notificationIds) {
      setNotifications(prev => prev.filter(n => !notificationIds.includes(n.id)));
    } else {
      setNotifications([]);
    }

    // Update via API
    axios.delete(`${API_URL}/api/notifications`, {
      data: { userId, notificationIds }
    }).catch(error => console.error('Error clearing notifications:', error));
  }, [userId, API_URL]);

  // Setup effect
  useEffect(() => {
    console.log('🚀 useNotifications effect triggered:', { userId, hasUserId: !!userId });
    
    if (userId) {
      console.log('👤 User ID exists, starting setup...');
      fetchNotifications();
      connectWebSocket();
      
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } else {
      console.log('❌ No user ID, skipping WebSocket connection');
    }

    return () => {
      console.log('🧹 Cleaning up useNotifications');
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [userId, fetchNotifications, connectWebSocket]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    clearNotifications,
    refetch: fetchNotifications,
    forceReconnect
  };
};

// Add a global test function for debugging
if (typeof window !== 'undefined') {
  (window as any).testWebSocket = () => {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const WS_URL = API_URL.replace(/^http/, 'ws') + '/ws';
    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token || '')}`;
    
    console.log('🧪 Manual WebSocket test:', {
      token: token?.substring(0, 20) + '...',
      API_URL,
      WS_URL,
      wsUrl: wsUrl.substring(0, 50) + '...'
    });
    
    const testWs = new WebSocket(wsUrl);
    
    testWs.onopen = () => {
      console.log('✅ Manual test: WebSocket connected!');
      testWs.close();
    };
    
    testWs.onerror = (error) => {
      console.error('❌ Manual test: WebSocket error:', error);
    };
    
    testWs.onclose = (event) => {
      console.log('🔌 Manual test: WebSocket closed:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
    };
    
    return testWs;
  };
}

export default useNotifications; 